import React, { createContext, useContext, useState, useCallback } from 'react';
import hi from '../i18n/translations';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(
    () => localStorage.getItem('cosmicLang') || 'en'
  );

  const toggleLang = useCallback(() => {
    setLang(prev => {
      const next = prev === 'en' ? 'hi' : 'en';
      localStorage.setItem('cosmicLang', next);
      return next;
    });
  }, []);

  /**
   * Translate a string.
   * - In English mode: returns the string as-is.
   * - In Hindi mode: looks up the Hindi dictionary, falls back to original.
   */
  const t = useCallback((str, vars = {}) => {
    if (!str) return str;
    
    let translated = typeof str === 'string' ? str : String(str);

    // 1. Direct match lookup
    if (lang === 'hi') {
      if (hi[str]) {
        translated = hi[str];
      } else {
        // 2. Dynamic translation fallback for Yogas and Aspects if no direct match
        const dynamicPhrases = [
          ["'s stone appears in recommended list (not avoid) — it rules Lagna/5th/9th which overrides 8th lordship.", " का रत्न अनुशंसित सूची में है (बचें नहीं) - यह लग्न/5वें/9वें भाव का स्वामी है जो 8वें भाव के स्वामित्व को खत्म कर देता है।"],
          ["'s stone appears in recommended list (not avoid) — it rules Lagna/5th/9th which overrides 12th lordship.", " का रत्न अनुशंसित सूची में है (बचें नहीं) - यह लग्न/5वें/9वें भाव का स्वामी है जो 12वें भाव के स्वामित्व को खत्म कर देता है।"],
          ["'s stone appears in recommended list — it rules Lagna/5th/9th which overrides 6th lordship.", " का रत्न अनुशंसित सूची में है - यह लग्न/5वें/9वें भाव का स्वामी है जो 6वें भाव के स्वामित्व को खत्म कर देता है।"],
          ["Mahadasha — ", "महादशा — "],
          [" — Vipreet Raja Yoga. The planet's challenging energies are neutralized and may bring unexpected gains or reversals of fortune.", " — विपरीत राज योग। ग्रह की चुनौतीपूर्ण ऊर्जाएं बेअसर हो जाती हैं और अप्रत्याशित लाभ या भाग्य में उलटफेर ला सकती हैं।"],
          [" is in a Kendra — Neecha Bhanga", " केंद्र में है — नीच भंग"],
          [" is in Kendra from Moon — Neecha Bhanga", " चंद्रमा से केंद्र में है — नीच भंग"],
          [" is Exalted in Navamsha — Neecha Bhanga", " नवांश में उच्च का है — नीच भंग"],
          [" (exalted in this sign)", " (इस राशि में उच्च का)"],
          [" aspect (grace) ↑", " की दृष्टि (कृपा) ↑"],
          [" aspect (discipline) ↓", " की दृष्टि (अनुशासन) ↓"],
          [" and is placed in house ", " और भाव में स्थित है "],
          [" — Neecha Bhanga", " — नीच भंग"],
          [" (dispositor)", " (डिस्पोज़िटर)"],
          [" conjunct ", " की युति "],
          [" rules the ", " स्वामी है "],
          [" (dusthana)", " (दुस्थान)"]
        ];

        const planets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];

        for (const [eng, hindi] of dynamicPhrases) {
          if (translated.includes(eng)) {
            translated = translated.split(eng).join(hindi);
          }
        }

        for (const p of planets) {
          if (translated.includes(p)) {
            const regex = new RegExp(`\\b${p}\\b`, 'g');
            translated = translated.replace(regex, hi[p] || p);
          }
        }
      }
    }

    // 3. Variable Replacement
    if (vars && typeof vars === 'object') {
      Object.keys(vars).forEach(key => {
        let val = vars[key];
        // Translate the variable itself if we are in hindi mode and a translation exists
        if (lang === 'hi' && hi[val]) {
          val = hi[val];
        }
        translated = translated.split(`{{${key}}}`).join(val);
      });
    }

    return translated;
  }, [lang]);

  /**
   * Translate a planet name (convenience wrapper, same as t() but semantic).
   */
  const tp = useCallback((planetName) => t(planetName), [t]);

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t, tp }}>
      {children}
    </LanguageContext.Provider>
  );
};

/** Hook: access language context in any component */
export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside <LanguageProvider>');
  return ctx;
};

export default LanguageContext;
